import { ActivityLog } from '@kalos/kalos-rpc';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ActivityLogClientService } from '../../tools/helpers';
import { queryKeys } from './constants';
import { type EntityFilter } from './utils';

export type ActivityLogFilter = EntityFilter<ActivityLog>;
export const useBatchGetActivityLogQuery = function ({
  filter = {},
  enabled,
}: {
  filter?: ActivityLogFilter;
  enabled?: boolean;
}) {
  return useQuery({
    queryFn: async () => {
      return await ActivityLogClientService.BatchGet(ActivityLog.create(filter));
    },
    queryKey: [
      queryKeys.activityLog.root,
      queryKeys.activityLog.list,
      queryKeys.activityLog.getHash(filter),
    ],
    enabled,
  });
};

export const useActivityLogCreateMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (req: ActivityLog) => ActivityLogClientService.Create(req),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [queryKeys.activityLog.root] }),
  });
};

/** Single-row ActivityLog.Get (e.g. latest for an event when orderBy activity_id DESC). */
export const useGetActivityLogQuery = function ({
  filter = {},
  enabled,
}: {
  filter?: ActivityLogFilter;
  enabled?: boolean;
}) {
  return useQuery({
    queryFn: async () => {
      const res = await ActivityLogClientService.Get(ActivityLog.create(filter));
      if (res === undefined) {
        throw new Error('Failed to load activity log');
      }
      return res.id > 0 ? res : null;
    },
    queryKey: [queryKeys.activityLog.root, 'get', queryKeys.activityLog.getHash(filter)],
    enabled,
  });
};
